import { all, call, put, select, takeLatest } from "redux-saga/effects";
import api from "../../utils/api";
import { fetchBodyRegionsFailure, fetchBodyRegionsSuccess } from "./actions";
import { BODY_REGIONS_STORAGE_KEY, FETCH_BODY_REGIONS } from "./constants";

/**
 * The body map's vocabulary + artwork, fetched once per session.
 *
 * Cached in localStorage keyed on the payload's own `version` (the API's content
 * hash of every region, shape and legacy alias). A cached copy paints the map
 * immediately; the network copy then confirms it, and only a version change
 * costs a re-render. If the request fails we keep whatever we have — a stale map
 * is a working map, and a blank one loses a nurse her wound sites.
 */
const readCache = () => {
  try {
    const raw = window.localStorage.getItem(BODY_REGIONS_STORAGE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    return cached?.version && cached?.regions ? cached : null;
  } catch (e) {
    return null;
  }
};

const writeCache = (payload) => {
  try {
    window.localStorage.setItem(
      BODY_REGIONS_STORAGE_KEY,
      JSON.stringify(payload),
    );
  } catch (e) {
    // Quota / private mode — the in-memory copy still serves this session.
  }
};

export function* fetchBodyRegionsSaga({ onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const store = yield select((state) => state?.BodyRegionsReducer);

  // Already holding the served payload this session — every body map on the page
  // shares it, so re-fetching it per mount would be one request per widget.
  if (store?.isLive) {
    yield put(fetchBodyRegionsSuccess(store.payload));
    typeof onSuccess === "function" && onSuccess(store.payload);
    return;
  }

  const cached = readCache();
  if (cached) {
    yield put(fetchBodyRegionsSuccess(cached));
  }

  try {
    const res = yield call(api(token).get, "/api/body-regions");
    const payload = res?.data?.data ?? res?.data;

    if (!payload?.regions || !payload?.shapes || !payload?.order) {
      throw new Error("Malformed body-regions payload");
    }

    if (!cached || cached.version !== payload.version) {
      writeCache(payload);
      yield put(fetchBodyRegionsSuccess(payload));
    }

    typeof onSuccess === "function" && onSuccess(payload);
  } catch (e) {
    // Silent: the bundled fallback (or the cached copy) is already rendering.
    yield put(fetchBodyRegionsFailure());
  }
}

export function* fetchBodyRegionsFlow() {
  yield takeLatest(FETCH_BODY_REGIONS, fetchBodyRegionsSaga);
}

export default function* BodyRegionsSaga() {
  yield all([fetchBodyRegionsFlow()]);
}
